import { NormalizedFieldState, NormalizedFormState, NormalizedSection } from '../config.models';
import { findSectionById } from '../configs/base-utils';

/**
 * Utility to find a field by its path string
 */
export function findFieldByPath(state: NormalizedFormState, path: string): NormalizedFieldState | null {
    if (!path) return null;

    const parts = path.split('.');
    let currentSection: NormalizedSection | undefined;
    let currentField: NormalizedFieldState | null = null;

    // First part should be section ID
    const sectionId = parts[0];
    currentSection = state.config.sections.find((s) => s.id === sectionId);
    if (!currentSection) return null;

    // Process remaining path parts
    for (let i = 1; i < parts.length; i++) {
        const part = parts[i];

        // Handle array notation [index]
        if (part.includes('[')) {
            // Extract array section ID and index
            const arrayMatch = part.match(/^([^\[]+)\[(\d+)\]$/);
            if (arrayMatch) {
                const [_, arraySectionId, indexStr] = arrayMatch;
                const index = parseInt(indexStr, 10);

                // Find the field that contains this array section
                currentField = currentSection.fields[arraySectionId];
                if (!currentField || currentField.type !== 'section' || !currentField.section) {
                    return null;
                }

                // Switch to the array section
                currentSection = currentField.section;

                // Get the row at specified index
                if (!currentSection.rows || index >= currentSection.rows.length) {
                    return null;
                }

                // For the next part, we'll look within this row's fields
                continue;
            }
            return null;
        }

        // Regular field access
        if (currentSection.fields && currentSection.fields[part]) {
            currentField = currentSection.fields[part];

            // If this is a section field and there are more path parts,
            // dive into the nested section
            if (currentField.type === 'section' && i < parts.length - 1 && currentField.section) {
                currentSection = currentField.section;
            } else if (i === parts.length - 1) {
                // We found our target field
                return currentField;
            }
        } else {
            return null;
        }
    }

    return currentField;
}

/**
 * Efficiently finds an array section by ID throughout the form structure.
 * This specialized finder is optimized for fast array section lookup.
 */
export function findArraySectionById(formState: NormalizedFormState, sectionId: string): NormalizedSection | null {
    // Direct section check at top level first (most common case)
    const topLevelSection = formState.config.sections.find((s) => s.id === sectionId);
    if (topLevelSection) {
        return topLevelSection;
    }

    // Check if it's inside a section field
    for (const section of formState.config.sections) {
        // Fast path - search only fields that are sections
        for (const fieldId in section.fields) {
            const field = section.fields[fieldId];

            if (field.type === 'section' && field.section) {
                // Check if this is the section we're looking for
                if (field.section.id === sectionId) {
                    return field.section;
                }

                // If not, search inside this nested section
                const result = findNestedArraySection(field.section, sectionId);
                if (result) {
                    return result;
                }
            }
        }
    }

    return null;
}

/**
 * Helper for recursively searching through nested sections.
 * Uses a depth-first search optimized for section finding.
 */
function findNestedArraySection(section: NormalizedSection, targetId: string): NormalizedSection | null {
    // Check if this section contains any section fields
    for (const fieldId in section.fields) {
        const field = section.fields[fieldId];

        if (field.type === 'section' && field.section) {
            // Check if this is the section we're looking for
            if (field.section.id === targetId) {
                return field.section;
            }

            // If not, search inside this nested section
            const result = findNestedArraySection(field.section, targetId);
            if (result) {
                return result;
            }
        }
    }

    return null;
}

/**
 * Generates the full path to a section field using field names (not IDs)
 * @param formState The normalized form state
 * @param section The section to generate a path for
 * @returns The complete path string using field names
 */
export function generateSectionPath(formState: NormalizedFormState, section: NormalizedSection): string {
    // For a top-level section, return its ID as the path
    if (!section.parent) {
        return section.id; // Return section ID instead of empty string
    }

    // Find the parent section
    const parentSection = findSectionById(formState, section.parent.sectionId);
    if (!parentSection) {
        return section.id; // Fallback to section ID if parent not found
    }

    // Find the field in the parent section that references this section
    let fieldName = '';
    for (const fieldId in parentSection.fields) {
        const field = parentSection.fields[fieldId];
        if (field.type === 'section' && field.section && field.section.id === section.id) {
            fieldName = field.name;
            break;
        }
    }

    if (!fieldName) {
        return section.id; // Fallback to section ID if field name not found
    }

    // If parent has its own parent, recursively build the path
    if (parentSection.parent) {
        const parentPath = generateSectionPath(formState, parentSection);
        return parentPath ? `${parentPath}.${fieldName}` : fieldName;
    }

    return `${parentSection.id}.${fieldName}`;
}
